import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { NESTED_IN_MODAL_Z } from './topLayerZ';

import type { CSSProperties, RefObject } from 'react';

const MARGIN = 4;
// Above the highest in-app layers — including modal backdrops — so an anchored
// dropdown opened from inside a modal overlays it instead of rendering behind.
// 1000011 is the documented "above all modal backdrops" tier (modal backdrop is
// 1000010); see docs/design-system.md "Depth Layering".
//
// PLAT-11514 — this RESTS at 1000011 but must follow the top-layer lift. The
// Translation Review's clip-preview entry mounts at
// `CLIP_PREVIEW_Z.translationReview` (1000030) and raises the shared overlay
// vars to `TOP_LAYER_Z` while open; a dropdown pinned to 1000011 renders
// underneath it and simply never appears. Reading the custom property inline
// keeps the resting value for every other surface and rises with the lift.
//
// It rises with the MODAL BACKDROP, not with the context-menu tier: lifted,
// context menus sit at 1000040 and modals at 1000041, so a dropdown on the
// context-menu var opened from a modal on that surface (the Dub modal's voice
// picker, from inside the review) painted behind the modal. One step above the
// backdrop is 1000011 at rest — unchanged — and 1000042 when lifted.
const Z_INDEX_CSS = NESTED_IN_MODAL_Z;

/**
 * Clearance an `auto`-placed panel keeps between its bottom edge and the window
 * edge. Fitting by a handful of pixels reads as falling off the screen even
 * though nothing is clipped, so the fit test asks for room *plus* this before it
 * leaves a panel below its trigger.
 */
const AUTO_FLIP_CLEARANCE = 24;

/**
 * Positions a dropdown menu in a body-level portal, by default directly
 * **below** its trigger and overlaying page content. Mirrors the ColorPicker
 * placement behaviour: below-only, and when there isn't room below it clamps
 * the height and lets the menu scroll internally. Pass `placement` to opt a
 * menu into opening upward instead — see the option's own note.
 *
 * It also **owns dismissal** — click outside (trigger *or* menu), scroll,
 * resize, Escape. This is required once the menu portals out of the
 * component's own DOM subtree: a wrapper-scoped click-outside hook would
 * treat clicks inside the (now body-level) menu as "outside" and close it
 * before the click registers.
 *
 * Usage:
 *   const triggerRef = useRef<HTMLDivElement>(null);
 *   const { menuRef, style } = useAnchoredDropdown(open, triggerRef, close);
 *   <Trigger ref={triggerRef} onClick={() => setOpen(o => !o)} />
 *   {open && style &&
 *     createPortal(<Menu ref={menuRef} style={style}>…</Menu>, document.body)}
 */
interface AnchoredDropdownOptions {
  /**
   * Fixed panel width (px). Defaults to the trigger's width (select-style
   * menus). Set this for a wide panel anchored to a narrow trigger — e.g. the
   * Export overflow menu hanging off the small Export button.
   */
  width?: number;
  /**
   * Horizontal alignment relative to the trigger. `start` (default) aligns the
   * panel's left edge to the trigger's left; `end` aligns the panel's right
   * edge to the trigger's right (for right-anchored triggers).
   */
  align?: 'start' | 'end';
  /**
   * Vertical placement. `below` (default) drops the panel under the trigger;
   * `above` opens it upward (for a trigger near the bottom, e.g. a footer
   * button); `auto` prefers below and flips up only when the panel's measured
   * content cannot fit below — with `AUTO_FLIP_CLEARANCE` to spare, so it never
   * hugs the window edge — and there is more room above. When opening above,
   * the panel's bottom anchors to the trigger's top and it grows upward,
   * scrolling internally past the available space.
   */
  placement?: 'below' | 'above' | 'auto';
  /**
   * PLAT-12049 — the panel's z-index, for a trigger that lives inside an
   * overlay above the modal tier (an anchored popover): pass
   * `ABOVE_ANCHORED_POPOVER_Z`. Default: one above the modal backdrop.
   */
  zIndex?: string | number;
}

export function useAnchoredDropdown(
  isOpen: boolean,
  triggerRef: RefObject<HTMLElement>,
  onClose: () => void,
  /**
   * Optional cap on the menu height (px). The menu is sized to its content
   * up to `min(spaceBelowViewport, maxHeightCap)`, scrolling internally past
   * that. Omit to fill the available space below the trigger.
   */
  maxHeightCap?: number,
  options?: AnchoredDropdownOptions,
) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [style, setStyle] = useState<CSSProperties | null>(null);

  // Keep the latest onClose without re-subscribing the listeners every render
  // (callers commonly pass an inline arrow).
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useLayoutEffect(() => {
    if (!isOpen || !triggerRef.current) {
      setStyle(null);
      return;
    }
    const rect = triggerRef.current.getBoundingClientRect();
    const width = options?.width ?? rect.width;
    // Align the panel's left or right edge to the trigger, clamped to the
    // viewport so a right-anchored wide panel never spills off-screen.
    const rawLeft = options?.align === 'end' ? rect.right - width : rect.left;
    const left = Math.max(
      MARGIN,
      Math.min(rawLeft, window.innerWidth - width - MARGIN),
    );

    const spaceBelow = window.innerHeight - rect.bottom - MARGIN;
    const spaceAbove = rect.top - MARGIN;
    const placement = options?.placement ?? 'below';
    // `auto` opens downward here and corrects itself below, once the panel has
    // rendered and its real height is measurable.
    const openAbove = placement === 'above';

    const spaceForMenu = openAbove ? spaceAbove : spaceBelow;
    const maxHeight = Math.max(
      maxHeightCap ? Math.min(spaceForMenu, maxHeightCap) : spaceForMenu,
      MARGIN,
    );

    setStyle({
      position: 'fixed',
      // Opening above anchors the panel's bottom to the trigger's top so it
      // grows upward; opening below anchors its top under the trigger.
      ...(openAbove
        ? { bottom: window.innerHeight - rect.top + MARGIN }
        : { top: rect.bottom + MARGIN }),
      left,
      width,
      maxHeight,
      overflowY: 'auto',
      zIndex: options?.zIndex ?? Z_INDEX_CSS,
      boxSizing: 'border-box',
    });
  }, [
    isOpen,
    triggerRef,
    maxHeightCap,
    options?.zIndex,
    options?.width,
    options?.align,
    options?.placement,
  ]);

  // Vertical correction, for `auto` only: flip a below-placed panel above when
  // its content does not fit under the trigger with a little room to spare. Deferred to a second pass
  // because the height isn't knowable before the panel renders — item count,
  // wrapped labels and CSS all decide it — and a hardcoded estimate lands wrong
  // by a few pixels exactly at the boundary where the flip matters.
  //
  // `scrollHeight`, not the rect: a below-placed panel is already clamped to the
  // space below and scrolls internally, so its *rect* always fits by
  // construction — a two-item menu 8px short of room renders as a scrolling
  // sliver rather than overflowing. The content is what doesn't fit.
  //
  // Runs in a layout effect so the flip lands before paint (no visible jump),
  // and only ever below -> above: the `bottom` guard makes a flipped panel final
  // for as long as it is open, so this cannot oscillate.
  useLayoutEffect(() => {
    if ((options?.placement ?? 'below') !== 'auto') return;
    if (!isOpen || !style || !menuRef.current || !triggerRef.current) return;
    if (style.bottom !== undefined) return;

    const content = menuRef.current.scrollHeight;
    // The menu will never render taller than maxHeightCap, so use the capped
    // height for the fit test — not the full scrollHeight.
    const effectiveHeight = maxHeightCap
      ? Math.min(content, maxHeightCap)
      : content;
    const rect = triggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom - MARGIN;
    const spaceAbove = rect.top - MARGIN;
    // Staying put when above is no roomier keeps the panel from flipping into a
    // worse spot — a clamped, scrolling menu below beats one clamped tighter up.
    if (effectiveHeight + AUTO_FLIP_CLEARANCE <= spaceBelow) return;
    if (spaceAbove <= spaceBelow) return;

    const maxHeight = Math.max(
      maxHeightCap ? Math.min(spaceAbove, maxHeightCap) : spaceAbove,
      MARGIN,
    );
    setStyle(prev =>
      prev
        ? {
            ...prev,
            top: undefined,
            bottom: window.innerHeight - rect.top + MARGIN,
            maxHeight,
          }
        : prev,
    );
  }, [isOpen, style, triggerRef, maxHeightCap, options?.placement]);

  // Second pass: re-clamp horizontally against the menu's *actual* rendered
  // width. Menu CSS can override the inline width the first pass clamped
  // against (e.g. the translation language menu's `width: max-content
  // !important`), letting a wide menu escape past the right viewport edge
  // (PLAT-11143). When the real width doesn't fit to the right, open leftward
  // (right edge on the trigger's right edge), then clamp to the viewport.
  // Runs in a layout effect so the correction lands before paint; the
  // `left`-equality guard stops it from re-running once settled.
  useLayoutEffect(() => {
    if (!isOpen || !style || !menuRef.current || !triggerRef.current) return;
    const menuWidth = menuRef.current.getBoundingClientRect().width;
    const rect = triggerRef.current.getBoundingClientRect();
    const rawLeft =
      options?.align === 'end' ? rect.right - menuWidth : rect.left;
    const overflowsRight = rawLeft + menuWidth > window.innerWidth - MARGIN;
    const preferredLeft = overflowsRight ? rect.right - menuWidth : rawLeft;
    const left = Math.max(
      MARGIN,
      Math.min(preferredLeft, window.innerWidth - menuWidth - MARGIN),
    );
    if (left !== style.left) {
      setStyle(prev => (prev ? { ...prev, left } : prev));
    }
  }, [isOpen, style, triggerRef, options?.align]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      onCloseRef.current();
    };
    // Close when an *ancestor* (page / side panel) scrolls — that would
    // misalign the fixed-position menu. But ignore the menu's OWN internal
    // scroll: capped lists (e.g. the font-size dropdown) scroll inside
    // themselves, and closing on that scroll was the bug where the menu shut
    // the moment you scrolled it.
    const onScroll = (e: Event) => {
      if (menuRef.current?.contains(e.target as Node)) return;
      onCloseRef.current();
    };
    const onResize = () => onCloseRef.current();
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // Escape dismisses the menu and nothing else. This listener sits on
      // `document` (bubble), so it runs before the `window`-level Escape
      // handlers that modals use to close themselves — stopping propagation
      // here keeps a menu opened inside a modal (e.g. the comment reaction
      // picker in the Clip Preview modal) from taking the whole modal down
      // with it. Same idiom as the comment composers' Escape handling.
      e.stopPropagation();
      // Release the trigger too. Clicking it focused it *without* a ring, but
      // this keypress promotes it to `:focus-visible` in Chrome — so without
      // the blur it is left wearing the browser's default focus ring (and,
      // where opacity is keyed off `:focus-visible`, stuck visible) after its
      // menu is gone. No-op unless the trigger itself holds focus, so wrapper
      // triggers (SplitButton, the Dashboard search field) are unaffected.
      triggerRef.current?.blur();
      onCloseRef.current();
    };
    document.addEventListener('mousedown', onPointerDown);
    // Capture-phase scroll so we catch scrolls in any ancestor scroll
    // container, not just the window.
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
      document.removeEventListener('keydown', onKey);
    };
  }, [isOpen, triggerRef]);

  return { menuRef, style };
}
